import React, { createRef, FunctionComponent } from "react";
import styles from "./TextBanner.module.scss";
import TeaserBannerContent from "./TextBannerContent.model";

import { useInView } from "react-intersection-observer";
import combineClasses from "helpers/combine-classes";
import DynamicHeading from "components/DynamicHeading/DynamicHeading";
import formatDynamicTextColor from "blocks/helpers/formatDynamicTextColor";
import getProcessedRichtext from "blocks/helpers/getProcessedRichtext";
import getWrapperStyles from "blocks/helpers/getWrapperStyles";
import isBrowserContext from "helpers/isBrowserContext";
import formatDynamicTextBreak from "blocks/helpers/formatDynamicTextBreak";
import IconViewer from "components/IconViewer/IconViewer";
import BlockButton from "components/BlockButton/BlockButton";

const fontWeights = (props: any) => {
    const vars = ["taglinefontWeight", "headlineFontWeight", "secondHeadlineFontWeight", "textFontWeight"].reduce(
        (a, c) => {
            if (props[c]) {
                a[`--${c}`] = parseInt(props[c]);
            }
            return a;
        },
        {} as Record<string, number>
    );
    return vars;
};

const TextBanner: FunctionComponent<TeaserBannerContent> = (props) => {
    const processedHeadline = getProcessedRichtext(formatDynamicTextBreak(formatDynamicTextColor(props.content.headline)));
    const processedSecondHeadline = getProcessedRichtext(formatDynamicTextBreak(formatDynamicTextColor(props.content.secondHeadline)));
    const processedBodyText = getProcessedRichtext(props.content.text);
    const wrapperStyles = getWrapperStyles(props.settings);
    const processedTagline = getProcessedRichtext(formatDynamicTextBreak(props.content.tagline));
    const bgColor = props.settings?.backgroundColor?.color || "#d8e8c5";

    const { ref, inView } = useInView({ triggerOnce: true });
    const isBrowserClass = isBrowserContext() ? styles.isBrowser : undefined;
    const inViewClass = inView ? styles.isInView : undefined;
    const wrapperElementRef = createRef<HTMLDivElement>();

    const fontWeightVars = fontWeights(props.settings);

    const clickScrollHandler = () => {
        if (wrapperElementRef.current) {
            const elementBottom = wrapperElementRef.current.getBoundingClientRect().bottom + window.scrollY;

            const offset = 0;
            const scrollTarget = elementBottom - offset;

            window.scrollTo({
                top: scrollTarget,
                behavior: "smooth",
            });
        }
    };

    return (
        <div
            className={combineClasses([styles.wrapper, ...wrapperStyles])}
            ref={wrapperElementRef}
            style={{ backgroundColor: bgColor, ...fontWeightVars }}
        >
            <div className={combineClasses([styles.inner])}>
                <div
                    className={combineClasses([styles.content, isBrowserClass, inViewClass])}
                    ref={ref}
                >
    
                    <div className={styles.text_content}>
                        <span
                            className={styles.tagline}
                            dangerouslySetInnerHTML={processedTagline}
                        />
                        {/* Headline */}
                        {props.content.headline ? (
                            <DynamicHeading
                                className={styles.headline}
                                headlineSize={props.content.headlineSize}
                                headlineWeight={props.content.headlineWeight}
                                innerHtml={processedHeadline}
                            ></DynamicHeading>
                        ) : null}

                        {props.content.secondHeadline ? (
                            <DynamicHeading
                                className={styles.headline}
                                headlineSize={props.content.headlineSize}
                                headlineWeight={props.content.headlineWeight}
                                innerHtml={processedSecondHeadline}
                            ></DynamicHeading>
                        ) : null}

                        {/* Body Text */}
                        {processedBodyText ? (
                            <div
                                className={styles.bodyText}
                                dangerouslySetInnerHTML={processedBodyText}
                            ></div>
                        ) : null}

                        {/* Buttons */}
                        {props.content.buttons && props.content.buttons.length > 0 ? (
                            <div className={styles.buttons}>
                                {props.content.buttons.map((button) => {
                                    return (
                                        <BlockButton
                                            key={button.contentUdi}
                                            label={button?.label}
                                            link={button?.link}
                                            target={button?.target}
                                            backgroundColor={button?.backgroundColor}
                                            textColor={button?.textColor}
                                            borderColor={button?.borderColor}
                                            backgroundHoverColor={button?.backgroundHoverColor}
                                            hoverTextColor={button?.hoverTextColor}
                                        />
                                    );
                                })}
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
            {/* Scroll Button */}
            {props.content.scrollArrowToggle ? (
                <div
                    className={styles.scrollIcon}
                    onClick={clickScrollHandler}
                >
                    <IconViewer
                        name="small-arrow-down"
                        heightRatio={31.552 / 10.848}
                    />
                </div>
            ) : null}
        </div>
    );
};

export default TextBanner;
